import React, { useState, useEffect } from "react";
import "./SearchSection.css";
import apiClient from "../../../utils/axiosConfig";
import { getApiUrl, API_ENDPOINTS } from "../../../Configurations/config";
import Pagination from "../../Common/Pagination";
import {
  IconChatBubblesBtn,
  IconChevronLeftMuted,
  IconSearchWhite,
} from "../../Common/EmcureReactIcons";

const SEARCH_QUERY_STORAGE_KEY = "emcure_search_query";

const SearchSection = ({ onViewMessages }) => {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);

  // Clear stored search value when landing on search page (from nav or refresh)
  useEffect(() => {
    try {
      localStorage.removeItem(SEARCH_QUERY_STORAGE_KEY);
    } catch (e) {
      // ignore
    }
  }, []);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(20);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showResults, setShowResults] = useState(false);

  const fetchSearchResults = async (pageToLoad = 1) => {
    if (!query.trim()) return;

    try {
      setLoading(true);
      setError(null);

      const response = await apiClient.get(
        getApiUrl(API_ENDPOINTS.ADMIN.SEARCH.EXACT),
        {
          params: {
            q: query.trim(),
            page: pageToLoad,
            page_size: pageSize,
          },
        }
      );

      const data = response.data || {};
      setResults(data.results || []);
      setTotal(data.total || 0);
      setPage(data.page || pageToLoad);
      setShowResults(true);
    } catch (err) {
      setError("Something went wrong while searching. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    if (!query.trim() || loading) return;
    fetchSearchResults(1);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSearch();
    }
  };

  const handlePageChange = (newPage) => {
    if (newPage < 1) return;
    const totalPages = Math.max(1, Math.ceil(total / pageSize));
    if (newPage > totalPages) return;
    fetchSearchResults(newPage);
  };

  const renderLanding = () => (
    <div className="professional-welcome">
        <div className="chat-landing-brand-header">
          <div className="chat-landing-brand-logo">
            <IconSearchWhite width={32} height={32} />
          </div>
          <h1 className="chat-landing-brand-title">Search</h1>
          <p className="chat-landing-brand-subtitle">
          Discover insights across your Emcure data
          </p>
        </div>

        <div className="professional-input-wrapper">
          <div className="search-input-row">
            <input
              type="text"
              className="search-input"
              placeholder="Search across your data..."
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={handleKeyDown}
            />
            <button
              className="professional-send-btn search-send-btn"
              type="button"
              onClick={handleSearch}
              disabled={loading}
            >
              <IconSearchWhite width={18} height={18} />
            </button>
          </div>
          <div className="input-footer">
            <span className="input-hint">
              Press Enter to search, Shift + Enter for new line
            </span>
          </div>
        </div>
      </div>
  );

  const handleBackToLanding = () => {
    setShowResults(false);
    setResults([]);
    setTotal(0);
    setError(null);
    setPage(1);
  };

  const renderResults = () => {
    const totalPages = Math.max(1, Math.ceil(total / pageSize));

    return (
      <div className="search-results-view">
        <div className="admin-title-wrapper">
          <div className="admin-title-left">
            <button
              type="button"
              className="search-back-btn"
              onClick={handleBackToLanding}
              style={{ marginBottom: 4 }}
            >
              <IconChevronLeftMuted width={16} height={16} />
            </button>
            <h2 className="admin-title">Search Results</h2>
            <p className="admin-title-sub">
              {loading
                ? "Searching..."
                : `${total} result${total === 1 ? "" : "s"} for "${query}"`}
            </p>
          </div>
          <div className="admin-title-right">
            <div className="admin-title-actions">
              <div className="sessions-search-wrapper">
                <input
                  type="text"
                  className="sessions-search-input"
                  placeholder="Search across your data..."
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  onKeyDown={handleKeyDown}
                />
                <IconSearchWhite className="sessions-search-icon" width={16} height={16} />
              </div>
            </div>
          </div>
        </div>

        {error && <div className="search-error">{error}</div>}

        <div className="admin-list-table-shell">
          <div className="admin-list-table-scroll data-table-container admin-dashboard-data-table-container">
            <div className="data-table-layout search-results-data-table-layout">
              {loading ? (
                <div className="no-results">Searching...</div>
              ) : results.length === 0 ? (
                <div className="no-results">No results found for this query.</div>
              ) : (
                <table className="data-table">
                  <thead>
                    <tr>
                      <th className="search-timestamp-col">Timestamp</th>
                      <th className="search-role-col">Role</th>
                      <th className="search-userid-col">User ID</th>
                      <th className="search-sessionid-col">Session ID</th>
                      <th className="actions-col">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {results.map((row) => (
                      <tr key={row.message_id}>
                        <td className="search-timestamp-cell">{row.timestamp}</td>
                        <td className="search-role-cell">{row.role}</td>
                        <td className="search-userid-cell">{row.user_id}</td>
                        <td className="search-sessionid-cell">{row.session_id}</td>
                        <td className="actions-cell">
                          <button
                            type="button"
                            className="view-messages-btn"
                            onClick={() => {
                              if (!onViewMessages) return;
                              const sessionMessages = results.filter(
                                (r) => r.session_id === row.session_id
                              );
                              onViewMessages(
                                row.session_id,
                                row.user_id,
                                sessionMessages.length > 0 ? sessionMessages : undefined,
                                undefined
                              );
                            }}
                            title="View Chat History"
                          >
                            <IconChatBubblesBtn width={18} height={18} />
                            <span>Show Messages</span>
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>

          <div className="admin-table-pagination">
            <Pagination
              page={page}
              totalPages={totalPages}
              totalItems={total}
              itemsPerPage={pageSize}
              onPageChange={handlePageChange}
              showInfo={true}
            />
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="chat-container-wrapper search-page-wrapper">
      {showResults ? renderResults() : renderLanding()}
    </div>
  );
};

export default SearchSection;


